import { useEffect, useRef, useState } from "react";
import Konva from "konva";

// ===== theme sederhana (admin preview) =====
const themeColors = {
  warning: "#FFA319",
  black: "#101828",
  white: "#ffffff",
  stroke: "#D0D5DD",
  primary: "#3C6F7D",
};

// ===== util =====
function clampInt(n, min, max) {
  const x = Math.floor(Number(n));
  if (!Number.isFinite(x)) return min;
  if (x < min) return min;
  if (x > max) return max;
  return x;
}

function normalizePreset(preset, len, maxValue) {
  const arr = Array.isArray(preset) ? preset : [];
  const out = new Array(len).fill(0);
  for (let i = 0; i < len; i++) out[i] = clampInt(arr[i] ?? 0, 0, maxValue);
  return out;
}

// simple media query hook (tanpa MUI)
function useIsMobile(breakpointPx = 576) {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia(`(max-width:${breakpointPx}px)`).matches;
  });

  useEffect(() => {
    const mq = window.matchMedia(`(max-width:${breakpointPx}px)`);
    const onChange = (e) => setIsMobile(e.matches);
    // Safari lama: addListener/removeListener
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);

    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, [breakpointPx]);

  return isMobile;
}

export default function AdminShapePreview({
  variant = "box", // "box" | "triangle" | "boxhard"
  preset = [],
  readOnly = true,
  onChangeValues, // optional callback(values:number[]) ketika admin klik (readOnly=false)
  height, // optional override
  className,
  style,
}) {
  const isMobile = useIsMobile(576);
  const canvasHeight = height ?? (isMobile ? 360 : 460);

  // Keep latest callback without forcing Konva stage re-init
  const onChangeValuesRef = useRef(onChangeValues);

  useEffect(() => {
    onChangeValuesRef.current = onChangeValues;
  }, [onChangeValues]);

  const boxRef = useRef(null);
  const apiRef = useRef(null);
  const initVariantRef = useRef(null);

  const expectedLen = variant === "box" ? 8 : variant === "triangle" ? 4 : 16;
  const maxValue = 2; // 0=white, 1=orange, 2=pattern

  const [values, setValues] = useState(() =>
    normalizePreset(preset, expectedLen, maxValue)
  );
  const valuesRef = useRef(values);

  const PATTERN_URL = "/blue.jpg"; // fixed, sama seperti versi user

  const patternImgRef = useRef(null);
  const patternReadyRef = useRef(false);

  useEffect(() => {
    valuesRef.current = values;
  }, [values]);

  useEffect(() => {
    const next = normalizePreset(preset, expectedLen, maxValue);
    valuesRef.current = next;
    setValues(next);
    apiRef.current?.renderFromState?.();
  }, [preset, expectedLen]);

  // live update dari preset/variant
  useEffect(() => {
    let cancelled = false;

    const img = new Image();
    img.onload = () => {
      if (cancelled) return;
      patternImgRef.current = img;
      patternReadyRef.current = true;

      // kalau stage sudah ada, paksa rebuild biar pattern langsung kepakai
      apiRef.current?.rebuild?.();
      apiRef.current?.renderFromState?.();
    };

    img.onerror = () => {
      if (cancelled) return;
      patternImgRef.current = null;
      patternReadyRef.current = false;

      apiRef.current?.rebuild?.();
      apiRef.current?.renderFromState?.();
    };

    img.src = PATTERN_URL;

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const container = boxRef.current;
    if (!container) return;

    // 1) Kalau variant berubah, hancurkan stage lama
    if (apiRef.current && initVariantRef.current !== variant) {
      apiRef.current.destroy();
      apiRef.current = null;
    }

    // 2) Kalau sudah ada stage & variant sama, cukup render ulang dari state
    if (apiRef.current && initVariantRef.current === variant) {
      apiRef.current.renderFromState?.();
      apiRef.current.resize?.();
      return;
    }

    let rafId = 0;
    let ro = null;
    let disposed = false;

    const tryInit = () => {
      if (disposed) return;

      const w = container.clientWidth;
      const h = container.clientHeight;

      // anti blank: tunggu sampai container punya ukuran
      if (!w || !h) {
        rafId = requestAnimationFrame(tryInit);
        return;
      }

      initVariantRef.current = variant;
      container.style.touchAction = "none";

      const stage = new Konva.Stage({
        container,
        width: w,
        height: h,
      });

      const layer = new Konva.Layer();
      stage.add(layer);

      const setCanvasCursor = (cursor) => {
        const canvases = stage.container().querySelectorAll("canvas");
        canvases.forEach((c) => c.style.setProperty("cursor", cursor, "important"));
      };
      setCanvasCursor("default");

      const destroy = () => {
        try { ro && ro.disconnect(); } catch { }
        try { stage.destroy(); } catch { }
        try { layer.destroy(); } catch { }
      };

      const resize = () => {
        const c = boxRef.current;
        if (!c) return;

        const W = c.clientWidth;
        const H = c.clientHeight;
        if (!W || !H) return;

        stage.size({ width: W, height: H });
        rebuild();
      };

      // penting untuk tab/collapse/modal

      if (typeof ResizeObserver !== "undefined") {
        ro = new ResizeObserver(() => resize());
        ro.observe(container);
      }

      // ===== builder per shape =====
      let rebuild = () => { };
      let renderFromState = () => { };

      // admin preview: no click
      const bindActivate = (node, fn) => {
        if (readOnly) return;
        let lock = 0;
        const run = (e) => {
          const now = Date.now();
          if (now - lock < 250) return;
          lock = now;
          e.cancelBubble = true;
          fn();
        };
        node.on("pointerdown", run);
        node.on("tap", run);
        node.on("click", run);
      };

      // ===== applyFill (0 putih, 1 oranye, 2 pattern image) =====
      const applyFill = (shape, v) => {
        const vv = Number.isFinite(v) ? v : 0;

        if (vv === 0) {
          shape.fillPriority("color");
          shape.fill(themeColors.white);
          shape.fillPatternImage(null);
          return;
        }

        if (vv === 1) {
          shape.fillPriority("color");
          shape.fill(themeColors.warning);
          shape.fillPatternImage(null);
          return;
        }

        // vv === 2 -> pattern
        if (patternReadyRef.current && patternImgRef.current) {
          shape.fillPriority("pattern");
          shape.fillPatternRepeat("repeat");
          shape.fillPatternScale({ x: 1, y: 1 });
          shape.fillPatternImage(patternImgRef.current);
          // fill tetap diset sebagai fallback dasar
          shape.fill(themeColors.primary);
        } else {
          // kalau image belum ready / error: fallback warna biasa dulu
          shape.fillPriority("color");
          shape.fill(themeColors.primary);
          shape.fillPatternImage(null);
        }
      };

      // =========================
      // BOX (8)
      // =========================
      if (variant === "box") {
        const outerRects = [];
        const innerRects = [];

        const cellRect = ({ x, y, w, h, idx, arr }) => {
          const r = new Konva.Rect({
            x, y, width: w, height: h,
            stroke: themeColors.black,
            strokeWidth: 2,
          });

          const getVal = () => {
            const st = valuesRef.current || [];
            return arr === "outer" ? st[idx] : st[4 + idx];
          };

          applyFill(r, getVal() ?? 0);

          if (!readOnly) {
            r.on("pointerenter", () => setCanvasCursor("pointer"));
            r.on("pointerleave", () => setCanvasCursor("default"));
          }

          bindActivate(r, () => {
            const cur = [...(valuesRef.current || [])];
            const at = arr === "outer" ? idx : 4 + idx;
            cur[at] = ((cur[at] ?? 0) + 1) % 3;
            valuesRef.current = cur;
            setValues(cur);
            applyFill(r, cur[at]);
            layer.batchDraw();

            // Notify parent (e.g. QuestionsForm) supaya bisa sync ke Redux
            onChangeValuesRef.current?.(cur);
          });

          return r;
        };

        const build = () => {
          layer.destroyChildren();
          outerRects.length = 0;
          innerRects.length = 0;

          const W = stage.width();
          const H = stage.height();

          const size = Math.min(W, H) * 0.78;
          const ox = (W - size) / 2;
          const oy = (H - size) / 2;
          const half = size / 2;

          // outer 4
          [
            { x: ox, y: oy, i: 0 },
            { x: ox + half, y: oy, i: 1 },
            { x: ox, y: oy + half, i: 2 },
            { x: ox + half, y: oy + half, i: 3 },
          ].forEach((c) => {
            const rect = cellRect({ x: c.x, y: c.y, w: half, h: half, idx: c.i, arr: "outer" });
            outerRects.push(rect);
            layer.add(rect);
          });

          // borders outer
          layer.add(new Konva.Rect({ x: ox, y: oy, width: size, height: size, stroke: themeColors.black, strokeWidth: 2, listening: false }));
          layer.add(new Konva.Line({ points: [ox + half, oy, ox + half, oy + size], stroke: themeColors.black, strokeWidth: 2, listening: false }));
          layer.add(new Konva.Line({ points: [ox, oy + half, ox + size, oy + half], stroke: themeColors.black, strokeWidth: 2, listening: false }));

          // inner 4
          const inSize = size * 0.5;
          const ix = ox + (size - inSize) / 2;
          const iy = oy + (size - inSize) / 2;
          const iHalf = inSize / 2;

          [
            { x: ix, y: iy, i: 0 },
            { x: ix + iHalf, y: iy, i: 1 },
            { x: ix, y: iy + iHalf, i: 2 },
            { x: ix + iHalf, y: iy + iHalf, i: 3 },
          ].forEach((c) => {
            const rect = cellRect({ x: c.x, y: c.y, w: iHalf, h: iHalf, idx: c.i, arr: "inner" });
            innerRects.push(rect);
            layer.add(rect);
          });

          // borders inner
          layer.add(new Konva.Rect({ x: ix, y: iy, width: inSize, height: inSize, stroke: themeColors.black, strokeWidth: 2, listening: false }));
          layer.add(new Konva.Line({ points: [ix + iHalf, iy, ix + iHalf, iy + inSize], stroke: themeColors.black, strokeWidth: 2, listening: false }));
          layer.add(new Konva.Line({ points: [ix, iy + iHalf, ix + inSize, iy + iHalf], stroke: themeColors.black, strokeWidth: 2, listening: false }));

          layer.draw();
        };

        renderFromState = () => {
          const st = valuesRef.current || [];
          outerRects.forEach((r, i) => applyFill(r, st[i] ?? 0));
          innerRects.forEach((r, i) => applyFill(r, st[4 + i] ?? 0));
          layer.batchDraw();
        };

        rebuild = build;
        build();
      }

      // =========================
      // BOX COMPLEX (16 segitiga)
      // =========================
      if (variant === "boxhard") {
        const tris = [];

        const tri = (points, idx) => {
          const t = new Konva.Line({
            points,
            closed: true,
            stroke: themeColors.black,
            strokeWidth: 2,
          });

          applyFill(t, (valuesRef.current || [])[idx] ?? 0);

          if (!readOnly) {
            t.on("pointerenter", () => setCanvasCursor("pointer"));
            t.on("pointerleave", () => setCanvasCursor("default"));
          }

          bindActivate(t, () => {
            const cur = [...(valuesRef.current || [])];
            cur[idx] = ((cur[idx] ?? 0) + 1) % 3;
            valuesRef.current = cur;
            setValues(cur);
            applyFill(t, cur[idx]);
            layer.batchDraw();

            // Notify parent (e.g. QuestionsForm) supaya bisa sync ke Redux
            onChangeValuesRef.current?.(cur);
          });

          tris[idx] = t;
          layer.add(t);
        };

        const build = () => {
          layer.destroyChildren();

          const W = stage.width();
          const H = stage.height();

          const size = Math.min(W, H) * 0.78;
          const ox = (W - size) / 2;
          const oy = (H - size) / 2;
          const half = size / 2;
          const cx = ox + half;
          const cy = oy + half;

          // outer 8
          tri([ox, oy, cx, oy, cx, cy], 0);
          tri([ox, oy, ox, cy, cx, cy], 1);

          tri([cx, oy, ox + size, oy, cx, cy], 2);
          tri([ox + size, oy, ox + size, cy, cx, cy], 3);

          tri([ox, cy, cx, cy, ox, oy + size], 4);
          tri([cx, cy, ox, oy + size, cx, oy + size], 5);

          tri([cx, cy, ox + size, cy, ox + size, oy + size], 6);
          tri([cx, cy, ox + size, oy + size, cx, oy + size], 7);

          // garis outer
          layer.add(new Konva.Rect({ x: ox, y: oy, width: size, height: size, stroke: themeColors.black, strokeWidth: 2, listening: false }));
          layer.add(new Konva.Line({ points: [cx, oy, cx, oy + size], stroke: themeColors.black, strokeWidth: 2, listening: false }));
          layer.add(new Konva.Line({ points: [ox, cy, ox + size, cy], stroke: themeColors.black, strokeWidth: 2, listening: false }));
          layer.add(new Konva.Line({ points: [ox, oy, ox + size, oy + size], stroke: themeColors.black, strokeWidth: 2, listening: false }));
          layer.add(new Konva.Line({ points: [ox + size, oy, ox, oy + size], stroke: themeColors.black, strokeWidth: 2, listening: false }));

          // inner 8
          const inSize = size * 0.5;
          const ix = ox + (size - inSize) / 2;
          const iy = oy + (size - inSize) / 2;
          const iHalf = inSize / 2;
          const icx = ix + iHalf;
          const icy = iy + iHalf;

          tri([ix, iy, icx, iy, icx, icy], 8);
          tri([ix, iy, ix, icy, icx, icy], 9);

          tri([icx, iy, ix + inSize, iy, icx, icy], 10);
          tri([ix + inSize, iy, ix + inSize, icy, icx, icy], 11);

          tri([ix, icy, icx, icy, ix, iy + inSize], 12);
          tri([icx, icy, ix, iy + inSize, icx, iy + inSize], 13);

          tri([icx, icy, ix + inSize, icy, ix + inSize, iy + inSize], 14);
          tri([icx, icy, ix + inSize, iy + inSize, icx, iy + inSize], 15);

          // garis inner
          layer.add(new Konva.Rect({ x: ix, y: iy, width: inSize, height: inSize, stroke: themeColors.black, strokeWidth: 2, listening: false }));
          layer.add(new Konva.Line({ points: [icx, iy, icx, iy + inSize], stroke: themeColors.black, strokeWidth: 2, listening: false }));
          layer.add(new Konva.Line({ points: [ix, icy, ix + inSize, icy], stroke: themeColors.black, strokeWidth: 2, listening: false }));
          layer.add(new Konva.Line({ points: [ix, iy, ix + inSize, iy + inSize], stroke: themeColors.black, strokeWidth: 2, listening: false }));
          layer.add(new Konva.Line({ points: [ix + inSize, iy, ix, iy + inSize], stroke: themeColors.black, strokeWidth: 2, listening: false }));

          layer.draw();
        };

        renderFromState = () => {
          const st = valuesRef.current || [];
          for (let i = 0; i < 16; i++) {
            const t = tris[i];
            if (!t) continue;
            applyFill(t, st[i] ?? 0);
          }
          layer.batchDraw();
        };

        rebuild = build;
        build();
      }

      // =========================
      // TRIANGLE (4) — juga pakai applyFill (0/1/2)
      // =========================
      if (variant === "triangle") {
        const cells = [];

        const triPts = (a, b, c) => [a.x, a.y, b.x, b.y, c.x, c.y];

        const addCell = (points, idx) => {
          const s = new Konva.Line({
            points,
            closed: true,
            stroke: themeColors.black,
            strokeWidth: 2,
          });

          applyFill(s, (valuesRef.current || [])[idx] ?? 0);

          if (!readOnly) {
            s.on("pointerenter", () => setCanvasCursor("pointer"));
            s.on("pointerleave", () => setCanvasCursor("default"));
          }

          bindActivate(s, () => {
            const cur = [...(valuesRef.current || [])];
            cur[idx] = ((cur[idx] ?? 0) + 1) % 3;
            valuesRef.current = cur;
            setValues(cur);
            applyFill(s, cur[idx]);
            layer.batchDraw();

            // Notify parent (e.g. QuestionsForm) supaya bisa sync ke Redux
            onChangeValuesRef.current?.(cur);
          });

          cells[idx] = s;
          layer.add(s);
        };

        const build = () => {
          layer.destroyChildren();
          cells.length = 0;

          const W = stage.width();
          const H = stage.height();
          const side = Math.min(W, H) * 0.75;
          const hh = (Math.sqrt(3) / 2) * side;
          const cx = W / 2;
          const topY = (H - hh) / 2;

          const A = { x: cx, y: topY };
          const B = { x: cx - side / 2, y: topY + hh };
          const C = { x: cx + side / 2, y: topY + hh };

          const AB = { x: (A.x + B.x) / 2, y: (A.y + B.y) / 2 };
          const AC = { x: (A.x + C.x) / 2, y: (A.y + C.y) / 2 };
          const BC = { x: (B.x + C.x) / 2, y: (B.y + C.y) / 2 };

          // index: [TOP, LEFT, RIGHT, CENTER]
          addCell(triPts(A, AB, AC), 0);
          addCell(triPts(AB, B, BC), 1);
          addCell(triPts(AC, BC, C), 2);
          addCell(triPts(AB, AC, BC), 3);

          layer.add(new Konva.Line({
            points: triPts(A, B, C),
            closed: true,
            stroke: themeColors.black,
            strokeWidth: 2,
            listening: false,
          }));

          layer.draw();
        };

        renderFromState = () => {
          const st = valuesRef.current || [];
          for (let i = 0; i < 4; i++) {
            const s = cells[i];
            if (!s) continue;
            applyFill(s, st[i] ?? 0);
          }
          layer.batchDraw();
        };

        rebuild = build;
        build();
      }

      apiRef.current = { destroy, resize, rebuild, renderFromState };

      // render initial
      renderFromState();
      resize();
    };

    rafId = requestAnimationFrame(tryInit);

    return () => {
      disposed = true;
      cancelAnimationFrame(rafId);

      apiRef.current?.destroy?.();
      apiRef.current = null;
      initVariantRef.current = null;
    };
  }, [variant, readOnly]);

  // resize listener
  useEffect(() => {
    const onResize = () => apiRef.current?.resize?.();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return (
    <div className={className} style={{ padding: 12, width: "100%", ...(style || {}) }}>
      <div
        ref={boxRef}
        style={{
          width: "100%",
          height: canvasHeight,
          border: `1px solid ${themeColors.stroke}`,
          borderRadius: 8,
          overflow: "hidden",
          background: themeColors.white,
        }}
      />
    </div>
  );
}